import "./CustomizedTable.scss";

import React, { useEffect } from "react";
import type { Column } from "react-table";
import { useTable } from "react-table";

import type { IData } from "../../types";

export interface Props {
  columns: Column<IData>[];
  emptyText?: string;
  data: IData[];
  isLoading?: boolean;
  hiddenColumns?: string[];
  activeIndex?: number;
  onRowClick?: (row: IData) => void;
}

const CustomizedTable: React.FC<Props> = ({
  columns,
  emptyText,
  data,
  isLoading = false,
  hiddenColumns,
  activeIndex,
  onRowClick,
}) => {
  const {
    headerGroups,
    rows,
    prepareRow,
    getTableProps,
    getTableBodyProps,
    setHiddenColumns,
  } = useTable({
    columns,
    data,
    autoResetHiddenColumns: false,
  });

  useEffect(() => {
    setHiddenColumns(hiddenColumns || []);
  }, [hiddenColumns, setHiddenColumns]);

  const notEmpty = columns.length > 0 && Array.isArray(data) && data.length > 0;

  return (
    <div className="table-responsive pt-4">
      {notEmpty ? (
        <table
          className="table align-middle table-row-bordered fs-6 dataTable no-footer"
          {...getTableProps()}
          style={{ width: "100%" }}
        >
          <thead>
            {headerGroups.map(headerGroup => {
              const { key, role } = headerGroup.getHeaderGroupProps();
              return (
                <tr
                  className="text-start text-muted fw-bolder fs-7 text-uppercase gs-0"
                  key={key}
                  role={role}
                >
                  {headerGroup.headers.map(column => {
                    const { key, role } = column.getHeaderProps();

                    return (
                      <th
                        key={key}
                        role={role}
                        className={`custom-table-header ${key}`}
                      >
                        {column.render("Header")}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody className="text-gray-500" {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              const { key, role } = row.getRowProps();

              return (
                <tr
                  key={key}
                  role={role}
                  className={`${onRowClick ? "active-row" : ""} ${
                    activeIndex === i ? "active" : ""
                  }`}
                  onClick={(e: React.MouseEvent<HTMLTableRowElement>) => {
                    if (
                      onRowClick &&
                      !(e.target as HTMLElement).closest("button") &&
                      !(e.target as HTMLElement).closest("label")
                    ) {
                      onRowClick({
                        ...row.original,
                        index: i,
                      });
                    }
                  }}
                >
                  {row.cells.map(cell => {
                    const { key, role } = cell.getCellProps();
                    return (
                      <td
                        key={key}
                        role={role}
                        className={`custom-table-cell ${cell.column.id}`}
                        style={{
                          minWidth: cell.column.minWidth || "auto",
                          width: cell.column.width || "auto",
                        }}
                      >
                        {cell.render("Cell", {
                          row,
                        })}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        !isLoading && (
          <div className="d-flex text-center w-100 align-content-center justify-content-center">
            {emptyText || "No data"}
          </div>
        )
      )}
    </div>
  );
};

export { CustomizedTable, type IData };
